import React, { useEffect } from 'react';
import { Link, useParams } from 'react-router-dom';
import Navigation from '../../components/Navigation';
import Footer from '../../components/Footer';
import SEO from '../../components/SEO';
import { useLanguage } from '../../i18n/LanguageContext';
import { trackEvent } from '../../analytics';
import { publishedProjects } from './Projects.tsx';
import { allModels } from './ProductPage.tsx';
import {
  pageStyle, containerStyle, h1Style, introStyle, h2Style,
  bodyTextStyle, ctaButtonStyle, bulletDot, cardStyle,
  breadcrumbStyle, breadcrumbListStyle, breadcrumbLinkStyle
} from './pageStyles';

const BASE_URL = 'https://www.hestiadoors.com';

const ProjectCase = () => {
  const { slug } = useParams();
  const { t, pick, path, lang } = useLanguage();
  const project = publishedProjects.find((p: any) => p.slug === slug);

  useEffect(() => {
    if (project) trackEvent('project_view', { project: slug });
  }, [slug, project]);

  if (!project) {
    return (
      <div style={pageStyle}>
        <SEO title={`${t('productPage.notFound')} | Hestia Doors`} description={t('productPage.notFoundDesc')} noindex />
        <Navigation />
        <div style={{ ...containerStyle, textAlign: 'center' as const, padding: '6rem 1.5rem' }}>
          <h1 style={h1Style}>{t('productPage.notFound')}</h1>
          <Link to={path('/projects')} style={{ color: '#dfa926', fontWeight: 600 }}>{t('projects.backTo')}</Link>
        </div>
        <Footer />
      </div>
    );
  }

  const title = pick(project, 'title');
  const description = pick(project, 'description');
  const requirements = pick(project, 'technicalRequirements') as string[] | undefined;
  const usedProducts = (project.productSlugs || [])
    .map((s: string) => allModels.find((e: any) => e.model.slug === s))
    .filter(Boolean);

  const jsonLd = {
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@type': 'Article',
        headline: title,
        description,
        author: { '@type': 'Organization', name: 'Hestia Doors', url: BASE_URL },
        ...(project.images && project.images[0] ? { image: `${BASE_URL}${project.images[0].src}` } : {})
      },
      {
        '@type': 'BreadcrumbList',
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: t('productPage.breadcrumbHome'), item: `${BASE_URL}${lang === 'et' ? '/et' : '/'}` },
          { '@type': 'ListItem', position: 2, name: t('projects.title'), item: `${BASE_URL}${lang === 'et' ? '/et' : ''}/projects/` },
          { '@type': 'ListItem', position: 3, name: title }
        ]
      }
    ]
  };

  return (
    <div style={pageStyle}>
      <SEO
        title={`${title} | Hestia Doors`}
        description={description}
        canonicalPath={`/projects/${slug}`}
        jsonLd={jsonLd}
        ogType="article"
        ogImage={project.images && project.images[0] ? project.images[0].src : undefined}
      />
      <Navigation />

      <div style={containerStyle}>
        <nav aria-label="Breadcrumb" style={breadcrumbStyle}>
          <ol style={breadcrumbListStyle}>
            <li><Link to={path('/')} style={breadcrumbLinkStyle}>{t('productPage.breadcrumbHome')}</Link></li>
            <li style={{ color: '#6b7280' }}>/</li>
            <li><Link to={path('/projects')} style={breadcrumbLinkStyle}>{t('projects.title')}</Link></li>
            <li style={{ color: '#6b7280' }}>/</li>
            <li style={{ color: '#dfa926' }} aria-current="page">{title}</li>
          </ol>
        </nav>

        <h1 style={h1Style}>{title}</h1>
        <p style={{ color: '#9ca3af', marginBottom: '1.5rem' }}>
          {pick(project, 'country')}
          {project.customerPublic && project.customerName ? ` · ${project.customerName}` : ''}
          {project.completionDate ? ` · ${project.completionDate}` : ''}
        </p>
        <p style={introStyle}>{description}</p>

        {requirements && requirements.length > 0 && (
          <section style={{ marginBottom: '3rem' }}>
            <h2 style={h2Style}>{t('projects.requirements')}</h2>
            <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
              {requirements.map((r: string, i: number) => (
                <li key={i} style={{ display: 'flex', alignItems: 'flex-start', marginBottom: '0.5rem', ...bodyTextStyle }}>
                  <span style={bulletDot} />
                  <span>{r}</span>
                </li>
              ))}
            </ul>
          </section>
        )}

        {project.images && project.images.length > 0 && (
          <section style={{ marginBottom: '3rem', display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '1rem' }}>
            {project.images.map((img: any, i: number) => (
              <img key={i} src={img.src} alt={pick(img, 'alt')} loading="lazy"
                style={{ width: '100%', borderRadius: '0.75rem', display: 'block' }} />
            ))}
          </section>
        )}

        {usedProducts.length > 0 && (
          <section style={{ marginBottom: '3rem' }}>
            <h2 style={h2Style}>{t('projects.productsUsed')}</h2>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: '1rem' }}>
              {usedProducts.map((e: any) => (
                <Link key={e.model.slug} to={path(`/products/${e.model.slug}`)} style={{ ...cardStyle, padding: '1rem' }}>
                  <img src={e.model.image} alt={pick(e.model, 'name')} loading="lazy"
                    style={{ width: '100%', height: '120px', objectFit: 'cover' as const, borderRadius: '0.375rem', marginBottom: '0.75rem', background: 'linear-gradient(45deg, #3a3a3a, #1a1a1a)' }} />
                  <span style={{ color: '#dfa926', fontWeight: 600, fontSize: '0.9rem' }}>{pick(e.model, 'name')}</span>
                </Link>
              ))}
            </div>
          </section>
        )}

        <div style={{ textAlign: 'center' as const, padding: '2rem 0' }}>
          <p style={bodyTextStyle}>{t('projects.ctaTitle')}</p>
          <Link to={path('/contact')} style={ctaButtonStyle}>{t('projects.cta')}</Link>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default ProjectCase;
